import React, { useState } from "react";
import styled from "styled-components";
import gql from "graphql-tag";
import { Subscription } from "react-apollo";
import scales from "assets/img/scale-weight.svg";
import { httpClient } from "../../../settings/apolloClient";
import SUBSCRIBE_TO_SENSOR_CHANGES from "../graphql/subscriptions/subscribeToSensorMeasurementChanges.js";

// The current value lives in the httpClient cache (seeded by SENSORS_QUERY on
// mount) so it survives tab switches. The subscription below only writes new
// values into that cache; it is never the source of the displayed value. This
// mirrors how automation status / actuators are kept live and sidesteps the
// @absinthe/socket resubscribe race that used to leave scales stuck at 0 after
// remount.
const SENSOR_LAST_MEASUREMENT_FRAGMENT = gql`
  fragment SensorLastMeasurement on Sensor {
    lastMeasurement
  }
`;

// Styled components
const SensorContainer = styled.div`
  transition: all 0.3s ease-in;
  position: fixed;
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-content: flex-start;
  flex-wrap: nowrap;
  z-index: 2023;
  top: ${({ monitoringExpanded, expansionHeight }) => (monitoringExpanded ? `calc(${expansionHeight} + 120px)` : "120px")};
  width: 290px;
  right: -230px;
  max-height: ${({ monitoringExpanded, expansionHeight }) => (monitoringExpanded ? `calc(95vh - ${expansionHeight} - 120px)` : "alc(95vh - 120px)")}c;
  transform: ${({ showScales }) => (showScales ? "translate(-230px)" : "translate(0px)")};
  background-color: rgba(0, 0, 0, 0);
  display: ${({ controlMenuOpen }) => (controlMenuOpen ? "none" : "flex")};
`;

const ScaleIcon = styled.div`
  background-color: #2f4050;
  order: 1;
  padding: 10px;
  z-index: 2025;
  max-height: 60px;
  border-radius: 10px 0 0 10px;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.8);
  clip-path: inset(0px 0px -10px -10px);
  cursor: pointer;
`;

const ScalesContainer = styled.div`
  background-color: #2f4050;
  order: 2;
  padding: 10px;
  z-index: 2023;
  max-width: 230px;
  width: 230px;
  text-align: center;
  border-radius: 0 0 0 10px;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.8);
  clip-path: inset(0px 0px -10px -10px);
  color: #fff;

  p {
    color: #fff;
    text-align: center;
    margin: 20px;
    font-size: 15px;
    font-family: "Rubik", sans-serif;
  }
`;

const ScalesList = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  border-top: 1px lightgray solid;
  border-bottom: 1px lightgray solid;
  padding-top: 5px;
  max-height: ${({ controlMenuOpen }) => controlMenuOpen ? "calc(95vh - 460px)" : "calc(95vh - 200px)"};
`;

const Span = styled.span`
  width: 180px;
  border-radius: 10px;
  background-color: #fff;
  color: #2f4050;
  display: inline-block;
  margin-bottom: 20px;
`;

const H5 = styled.h5`
  margin-top: 3px;
  margin-bottom: 5px;
`;

const H2 = styled.h2`
  margin-top: 5px;
  margin-bottom: 0px;
  font-weight: bold;
`;

// Keeps the sensor value live by writing each measurement into the httpClient
// cache. Renders nothing; the value is displayed by <Sensor> reading the cache
// (via SENSORS_QUERY). Writing to the cache instead of rendering directly means
// the last value persists across tab switches even if this subscription is
// briefly torn down and recreated.
const SensorMeasurementSubscriber = ({ id }) => (
  <Subscription
    subscription={SUBSCRIBE_TO_SENSOR_CHANGES}
    fetchPolicy="no-cache"
    variables={{ id }}
    onSubscriptionData={({ subscriptionData }) => {
      const measurement = subscriptionData
        && subscriptionData.data
        && subscriptionData.data.subscribeToSensorMeasurementChanges;

      if (measurement === undefined || measurement === null) return;

      httpClient.writeFragment({
        id: `Sensor:${id}`,
        fragment: SENSOR_LAST_MEASUREMENT_FRAGMENT,
        data: {
          __typename: "Sensor",
          lastMeasurement: measurement,
        },
      });
    }}
  >
    {() => null}
  </Subscription>
);

const Sensor = ({ lastMeasurement, name, unit }) => {
  const value = (lastMeasurement != null ? lastMeasurement : 0).toFixed(1);
  const info = unit ? `${value} ${unit}` : lastMeasurement > 0 ? "ON" : "OFF";
  return (
    <Span>
      <H2>
        { info }
      </H2>
      <H5>{ name }</H5>
    </Span>
  );
};

// Main Component
export const Sensors = ({
  sensorsList,
  controlMenuOpen,
  monitoringExpanded,
  expansionHeight,
  onClick,
}) => {
  const [showScales, setShowScales] = useState(false);

  return (
    <SensorContainer
      controlMenuOpen={controlMenuOpen}
      showScales={showScales}
      monitoringExpanded={monitoringExpanded}
      expansionHeight={expansionHeight}
      onClick={onClick}
    >
      <ScaleIcon onClick={() => setShowScales(!showScales)}>
        <img src={scales} alt="Scale icon" width="40px" height="40px" />
      </ScaleIcon>
      <ScalesContainer>
        <p>BALANÇAS</p>
        <ScalesList controlMenuOpen={controlMenuOpen}>
          {sensorsList.map((sensor) => (
            <React.Fragment key={sensor.id}>
              <SensorMeasurementSubscriber id={sensor.id} />
              <Sensor
                name={sensor.name}
                unit={sensor.unit}
                lastMeasurement={sensor.lastMeasurement}
              />
            </React.Fragment>
          ))}
        </ScalesList>
      </ScalesContainer>
    </SensorContainer>
  );
};
